import { CHANGE_ORDER_KIND_LABELS, type ChangeOrderItem, type ChangeOrderKind, type PublicChangeOrder } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { errorMessage, get, post } from "../api";
import { Alert, Badge, Button, Dialog, Empty, Field, Loading, Panel, PanelHeader, SelectField, TextAreaField, cx, formatCents, formatDateTime } from "../components/ui";
import { useSession } from "../session";
import { RichText } from "../components/rich-text";
import { type Adopted, GuidedSigning } from "../components/guided-signing";
import { SendToCustomer } from "../components/email-dialog";

const STATUS: Record<ChangeOrderItem["status"], { label: string; tone: "ok" | "wait" | "off" | "brand" }> = {
  draft: { label: "Draft", tone: "wait" },
  sent: { label: "Sent to sign", tone: "brand" },
  approved: { label: "Approved", tone: "ok" },
  signed: { label: "Signed", tone: "ok" },
  declined: { label: "Declined", tone: "off" },
  void: { label: "Void", tone: "off" },
};

const signed = (c: number) => (c < 0 ? `−${formatCents(-c)}` : `+${formatCents(c)}`);

/** Change orders on the job's Proposals tab. */
export function JobChangeOrders({ jobId }: { jobId: string }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["change-orders", jobId], queryFn: () => get<ChangeOrderItem[]>(`/api/jobs/${jobId}/change-orders`) });
  const [adding, setAdding] = useState(false);
  const [sending, setSending] = useState<ChangeOrderItem | null>(null);
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ["change-orders", jobId] }), qc.invalidateQueries({ queryKey: ["job", jobId] })]);
  const act = useMutation({
    mutationFn: ({ id, what }: { id: string; what: "approve" | "void" }) => post<ChangeOrderItem>(`/api/change-orders/${id}/${what}`),
    onSuccess: refresh,
  });
  const edit = can("proposals.edit");
  return (
    <Panel>
      <PanelHeader
        title="Change orders"
        description="Approved supplements, added work, and credits. The customer signs only when what they pay goes up."
        action={edit && <Button className="px-3 py-1.5" onClick={() => setAdding(true)}>New change order</Button>}
      />
      {act.error && <div className="px-5 pt-3"><Alert>{errorMessage(act.error)}</Alert></div>}
      {sending && (
        <SendToCustomer
          kind="change_order"
          id={sending.id}
          jobId={jobId}
          note={sending.status === "sent" ? "This makes a new signing link; the old one stops working." : "This saves the change order's PDF to the job's files and makes the customer's signing link. Then you can check the email or text before it goes."}
          send={async () => {
            const c = await post<ChangeOrderItem>(`/api/change-orders/${sending.id}/send`);
            await refresh();
            return c.signUrl!;
          }}
          onClose={() => setSending(null)}
        />
      )}
      {list.isLoading ? <div className="px-5"><Loading /></div> : !list.data?.length ? <Empty>No change orders.</Empty> : (
        <ul>
          {list.data.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center gap-3 border-b border-mist px-5 py-3 text-sm last:border-0">
              <span className="font-semibold">CO {c.number}</span>
              <Badge tone={STATUS[c.status].tone}>{STATUS[c.status].label}</Badge>
              <span className="min-w-0 flex-1 truncate">{c.title}</span>
              <span title="Change to the contract">{signed(c.contractDeltaCents)}</span>
              {c.outOfPocketDeltaCents !== 0 && <span className="text-muted">customer {signed(c.outOfPocketDeltaCents)}</span>}
              {c.signatureName && <span className="text-muted">signed by {c.signatureName} {formatDateTime(c.signedAt)}</span>}
              {c.pdfUrl && <a href={c.pdfUrl} target="_blank" rel="noreferrer" className="text-brand hover:underline">PDF</a>}
              {edit && c.status === "draft" && (c.needsSignature ? (
                <Button variant="primary" className="px-3 py-1" onClick={() => setSending(c)}>Send to customer…</Button>
              ) : (
                <Button variant="primary" className="px-3 py-1" busy={act.isPending} onClick={() => act.mutate({ id: c.id, what: "approve" })}>Approve</Button>
              ))}
              {edit && c.status === "sent" && <Button className="px-3 py-1" onClick={() => setSending(c)}>Send again…</Button>}
              {edit && (c.status === "draft" || c.status === "sent") && <button className="text-muted hover:text-danger" onClick={() => window.confirm("Void this change order?") && act.mutate({ id: c.id, what: "void" })}>Void</button>}
            </li>
          ))}
        </ul>
      )}
      {adding && <NewChangeOrder jobId={jobId} onClose={() => setAdding(false)} onSaved={async () => (setAdding(false), await refresh())} />}
    </Panel>
  );
}

function NewChangeOrder({ jobId, onClose, onSaved }: { jobId: string; onClose: () => void; onSaved: () => void }) {
  const [kind, setKind] = useState<ChangeOrderKind>("supplement");
  const [title, setTitle] = useState("");
  const [lines, setLines] = useState([{ description: "", amount: "" }]);
  const [note, setNote] = useState("");
  const cents = lines.map((l) => (/^\d+(\.\d{1,2})?$/.test(l.amount.replace(/[$,\s]/g, "")) ? Math.round(Number(l.amount.replace(/[$,\s]/g, "")) * 100) : null));
  const ok = lines.every((l, i) => l.description.trim() && cents[i] !== null);
  const total = cents.reduce<number>((s, c) => s + (c ?? 0), 0);
  const save = useMutation({ mutationFn: () => post(`/api/jobs/${jobId}/change-orders`, { kind, title, note, lines: lines.map((l, i) => ({ description: l.description.trim(), amountCents: cents[i]! })) }), onSuccess: onSaved });
  return (
    <Dialog
      wide
      title="New change order"
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={save.isPending} disabled={!ok} onClick={() => save.mutate()}>Save draft · {kind === "credit" ? "−" : "+"}{formatCents(total)}</Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        <SelectField label="What kind" value={kind} onChange={(e) => setKind(e.target.value as ChangeOrderKind)} hint={kind === "supplement" ? "The carrier pays it; the customer doesn't sign." : kind === "added_work" ? "The customer pays it, so they sign." : "Money off; no signature needed."}>
          {(Object.keys(CHANGE_ORDER_KIND_LABELS) as ChangeOrderKind[]).map((k) => <option key={k} value={k}>{CHANGE_ORDER_KIND_LABELS[k]}</option>)}
        </SelectField>
        <Field label="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} />
        {lines.map((l, i) => (
          <div key={i} className="grid grid-cols-[1fr_140px_auto] items-start gap-2">
            <TextAreaField label={i === 0 ? "Description" : ""} aria-label="Description" rows={2} maxLength={4000} value={l.description} onChange={(e) => setLines(lines.map((x, k) => (k === i ? { ...x, description: e.target.value } : x)))} />
            <Field label={i === 0 ? "Amount" : ""} aria-label="Amount" inputMode="decimal" value={l.amount} onChange={(e) => setLines(lines.map((x, k) => (k === i ? { ...x, amount: e.target.value } : x)))} />
            <button className={cx("px-1 py-2 text-muted hover:text-danger", i === 0 && "mt-6")} aria-label="Remove line" disabled={lines.length === 1} onClick={() => setLines(lines.filter((_, k) => k !== i))}>✕</button>
          </div>
        ))}
        <div><Button variant="quiet" className="px-3 py-1.5" onClick={() => setLines([...lines, { description: "", amount: "" }])}>+ Line</Button></div>
        <TextAreaField label="Note for the customer (optional)" value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
      </div>
    </Dialog>
  );
}

/** The customer's change order page: review and sign (typed name) or decline. */
export function PublicChangeOrderPage() {
  const token = window.location.pathname.split("/")[2] ?? "";
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["public-co", token], queryFn: () => get<PublicChangeOrder>(`/api/public/change-orders/${token}`), retry: false });
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [adopted, setAdopted] = useState<Adopted | null>(null);
  const sign = useMutation({
    mutationFn: () =>
      post<PublicChangeOrder>(`/api/public/change-orders/${token}/sign`, adopted ? { name: adopted.name, consent: true, initials: adopted.initials, signature: adopted.signaturePng } : { name, consent }),
    onSuccess: (d) => {
      qc.setQueryData(["public-co", token], d);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
  });
  const decline = useMutation({ mutationFn: () => post<PublicChangeOrder>(`/api/public/change-orders/${token}/decline`, {}), onSuccess: (d) => qc.setQueryData(["public-co", token], d) });
  useEffect(() => {
    if (q.data) {
      document.documentElement.style.setProperty("--nmt-primary", q.data.company.primaryColor);
      if (!name) setName(q.data.customerName);
    }
  }, [q.data]); // eslint-disable-line react-hooks/exhaustive-deps
  if (q.isLoading) return <div className="grid min-h-dvh place-items-center"><Loading /></div>;
  if (q.error || !q.data) return <div className="mx-auto max-w-lg px-4 py-16"><Alert>{errorMessage(q.error) || "This link isn't valid."}</Alert></div>;
  const c = q.data;
  return (
    <div className="min-h-dvh bg-paper">
      <header className="bg-[var(--color-sidebar)] text-white">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-4 px-4 py-4">
          <div className="flex items-center gap-3">
            {c.company.logoUrl && <img src={c.company.logoUrl} alt="" className="h-9 w-auto rounded bg-white p-1" />}
            <span className="text-lg font-semibold">{c.company.name}</span>
          </div>
          <a href={`tel:${c.company.phone}`} className="text-sm text-white/80 hover:text-white">{c.company.phone}</a>
        </div>
      </header>
      <main className="mx-auto flex max-w-2xl flex-col gap-5 px-4 py-6">
        <div>
          <h1 className="text-2xl font-semibold">Change order {c.number}</h1>
          <p className="text-ink-soft">For {c.customerName}{c.address && ` · ${c.address}`}</p>
        </div>
        <section className="rounded-[var(--radius-panel)] bg-white px-5 py-4 text-sm shadow-[var(--shadow-card)]">
          <div className="mb-2 font-semibold">{c.title}</div>
          <table className="w-full">
            <tbody>
              {c.lines.map((l, i) => (
                <tr key={i} className="border-b border-mist">
                  <td className="py-2 whitespace-pre-wrap">{l.description}</td>
                  <td className="py-2 text-right">{c.kind === "credit" ? "−" : ""}{formatCents(l.amountCents)}</td>
                </tr>
              ))}
              <tr className="font-semibold"><td className="py-2">Change to the contract</td><td className="py-2 text-right">{signed(c.contractDeltaCents)}</td></tr>
              <tr className="text-base font-semibold"><td className="py-2">Change to what you pay</td><td className="py-2 text-right">{signed(c.outOfPocketDeltaCents)}</td></tr>
            </tbody>
          </table>
          {c.note && <RichText className="mt-2 text-ink-soft" text={c.note} />}
        </section>
        {c.status === "signed" ? (
          <Alert tone="ok">Signed by {c.signatureName} on {formatDateTime(c.signedAt)}. Thank you!</Alert>
        ) : c.status === "declined" ? (
          <Alert tone="info">You declined this change order. Call us at {c.company.phone} with any questions.</Alert>
        ) : c.signing ? (
          <>
            <Alert tone="info">Tap the yellow <b>Initial</b> box on each page, then <b>Sign here</b> and <b>Finish</b>.</Alert>
            <GuidedSigning
              documentUrl={`/api/public/change-orders/${token}/document.pdf`}
              tapUrl={`/api/public/change-orders/${token}/tap`}
              signing={c.signing}
              adopted={adopted}
              onAdopt={setAdopted}
              defaultName={c.customerName}
              consent="I agree to this change order and to sign it electronically. My electronic signature and initials are the legal equivalent of my handwritten signature, and I can download a signed copy."
              onChange={(signing) => qc.setQueryData(["public-co", token], { ...c, signing })}
              finish={
                <div className="flex flex-col items-end gap-1">
                  {sign.error && <span className="text-sm text-[var(--color-danger)]">{errorMessage(sign.error)}</span>}
                  <Button variant="primary" busy={sign.isPending} onClick={() => sign.mutate()}>Finish and sign</Button>
                </div>
              }
            />
            <div className="pb-6">
              {decline.error && <Alert>{errorMessage(decline.error)}</Alert>}
              <button className="text-sm text-muted hover:text-ink" onClick={() => window.confirm("Decline this change order?") && decline.mutate()}>Decline this change order</button>
            </div>
          </>
        ) : (
          <section className="flex flex-col gap-3 rounded-[var(--radius-panel)] bg-white px-5 py-5 shadow-[var(--shadow-card)]">
            {(sign.error || decline.error) && <Alert>{errorMessage(sign.error ?? decline.error)}</Alert>}
            <Field label="Type your full name to sign" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-0.5 size-4 accent-[var(--color-brand)]" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
              <span>I agree to this change order and to sign it electronically. My typed name is my signature.</span>
            </label>
            <div className="flex gap-3">
              <Button variant="primary" busy={sign.isPending} disabled={!consent || name.trim().length < 2} onClick={() => sign.mutate()}>Sign change order</Button>
              <button className="text-sm text-muted hover:text-ink" onClick={() => window.confirm("Decline this change order?") && decline.mutate()}>Decline</button>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
